import { ApiError } from "../api/http_client.js";
import { getContentConfig } from "../api/requests/player.requests.js";
import { claimPrizeShot, continuePrizeShot } from "../api/requests/prize_shot.requests.js";
import { adoptEconomyFromPlayer } from "../api/player_sync.js";
import { logRewardCoins, trackFeatureEvent } from "../analytics/feature_events.js";
import { logVirtualCurrencySpent } from "../analytics/appsflyer_events.js";
import { EventsConfigService } from "../events_config/events_config_service.js";
import { LocalMetrics } from "../offline/local_metrics.js";
import { OfflineEconomy } from "../offline/offline_economy.js";
import { OfflineJournal } from "../offline/offline_journal.js";
import { shouldGoLocal } from "../offline/offline_policy.js";
import { isNetworkishError } from "../utils/network_errors.js";
import { PersistentCache } from "../offline/persistent_cache.js";
import { ServerClock } from "../offline/server_clock.js";
import { StatisticsStorage } from "../storage/statistics_storage.js";
import {
  canContinue,
  createRound,
  DEFAULT_PRIZE_SHOT_CONFIG,
  dealNextBoard,
  isBatchOver,
  isBoardCleared,
  migrateRound,
  normalizePrizeShotConfig,
  popCell,
  prizeShotPeriod,
  prizesLeft,
  sumRewards,
  unclaimedRewards,
  wonRewards,
  type PrizeShotConfig,
  type PrizeShotReward,
  type PrizeShotRound,
} from "./prize_shot_rules.js";

const CONFIG_TYPE = "prize_shot";
const CONFIG_CACHE_KEY = "prize_shot_config_v1";
const ROUND_KEY = "prize_shot_round_v1";
/** Prizes a round left unclaimed when its period ended; the lobby hands them over. */
const PENDING_KEY = "prize_shot_pending_v1";

interface PrizeShotPending
{
  round_id: string;
  rewards: PrizeShotReward[];
}

/** Temporary product switch: keep Prize Shot visible and replayable in the lobby. */
export const PRIZE_SHOT_ALWAYS_ON = true;

export enum PrizeShotContinueResult
{
  Ok = "ok",
  InsufficientFunds = "insufficient_funds",
  Unavailable = "unavailable",
}

/** How a coin step is settled when the server does not take it. */
enum Settlement
{
  /** Offline or a network error: locally, and replayed from the journal later. */
  Journaled,
  /** The server has no such route yet: locally only. */
  LocalOnly,
  /** The server refused the step. */
  Refused,
}

/**
 * Prize Shot event. The config is the server content-config `prize_shot`
 * (404 keeps the bundled one); the round itself — board, popped balloons,
 * balls left — lives on the device in `bb_offline_prize_shot_round_v1`. Coins
 * move on the server (`prize-shot/continue`, `prize-shot/claim`); offline or on
 * a network error they move locally and the step is journaled, and while the
 * server has no such routes (404) they only move locally — the next player
 * sync then overwrites the balance. See CLAUDE.md «Prize Shot».
 */
class PrizeShotServiceImpl
{
  private config: PrizeShotConfig = this.readCachedConfig() ?? DEFAULT_PRIZE_SHOT_CONFIG;
  private config_loading: Promise<PrizeShotConfig> | null = null;
  private current: PrizeShotRound | null = this.readCachedRound();
  // OPT: Cache the parsed boundary used by the very hot round() accessor.
  // Config changes invalidate it, so period semantics remain unchanged.
  private current_period_start_ms = this.current ? Date.parse( this.current.round_id ) : NaN;
  private pending: PrizeShotPending | null = PersistentCache.read<PrizeShotPending>( PENDING_KEY );
  private rev = 0;
  private readonly listeners = new Set<() => void>();
  /** The server answered 404 on an economy route this session: settle locally. */
  private route_missing = false;

  public hasCachedConfig(): boolean
  {
    return PersistentCache.read<unknown>( CONFIG_CACHE_KEY ) !== null;
  }

  public getConfig(): PrizeShotConfig
  {
    return this.config;
  }

  public async refreshConfig(): Promise<PrizeShotConfig>
  {
    if ( this.config_loading )
      return this.config_loading;

    this.config_loading = ( async () =>
    {
      try
      {
        const raw = await getContentConfig<unknown>( CONFIG_TYPE );
        this.applyConfig( normalizePrizeShotConfig( raw ) );
        PersistentCache.write( CONFIG_CACHE_KEY, this.config );
      }
      catch ( e )
      {
        if ( e instanceof ApiError && e.status === 404 )
        {
          this.applyConfig( DEFAULT_PRIZE_SHOT_CONFIG );
          PersistentCache.remove( CONFIG_CACHE_KEY );
        }
        else
          console.warn( "[PrizeShot] config fetch failed", e );
      }
      finally
      {
        this.config_loading = null;
      }
      return this.config;
    } )();
    return this.config_loading;
  }

  public applyConfig( config: PrizeShotConfig ): void
  {
    const changed = JSON.stringify( config ) !== JSON.stringify( this.config );
    this.config = config;
    this.current_period_start_ms = NaN;
    if ( changed )
      this.notify();
  }

  /** Bumped on every change of the round; screens rebuild from it. */
  public revision(): number
  {
    return this.rev;
  }

  public onChange( listener: () => void ): () => void
  {
    this.listeners.add( listener );
    return () => this.listeners.delete( listener );
  }

  public nowMs(): number
  {
    return ServerClock.nowMs();
  }

  /** The round of the current period, dealt on first use. Null while the event is off. */
  public round(): PrizeShotRound | null
  {
    if ( !PRIZE_SHOT_ALWAYS_ON && !this.config.enabled )
      return null;

    const now = this.nowMs();
    // OPT: HUD and gameplay query the same round several times per frame. The
    // common path now avoids prizeShotPeriod(), Date allocation and ISO strings.
    if (
      this.current
      && Number.isFinite( this.current_period_start_ms )
      && now >= this.current_period_start_ms
      && now < this.current.ends_at_ms
    )
      return this.current;

    const period = prizeShotPeriod( this.config, now );
    const round_id = new Date( period.start_ms ).toISOString();
    if ( this.current?.round_id === round_id )
    {
      this.current_period_start_ms = period.start_ms;
      return this.current;
    }

    // Balls the last board left unused (all its prizes found early, or simply
    // not shot) move on to the new one.
    const carried_shots = this.current?.shots_left ?? 0;
    if ( this.current )
      this.stashUnclaimed( this.current );

    this.current = createRound( this.config, now, Math.floor( Math.random() * 0xFFFFFFFF ), carried_shots );
    this.current_period_start_ms = period.start_ms;
    this.persist();
    trackFeatureEvent( "prize_shot_started", this.eventProps( this.current ), `${round_id}` );
    return this.current;
  }

  /** The board lives the whole period, so the lobby shows the icon as long as there is a round. */
  public hasEvent(): boolean
  {
    return this.round() !== null;
  }

  public isUnlocked(): boolean
  {
    return PRIZE_SHOT_ALWAYS_ON || EventsConfigService.isEventUnlocked( "prize_shot" );
  }

  public isActive(): boolean
  {
    return this.hasEvent() && this.isUnlocked();
  }

  public timeLeftMs(): number
  {
    const round = this.round();
    return round ? Math.max( 0, round.ends_at_ms - this.nowMs() ) : 0;
  }

  public shotsLeft(): number
  {
    return this.round()?.shots_left ?? 0;
  }

  /** Charged when the ball leaves the cannon, so quitting mid-flight does not refund it. */
  public spendShot(): boolean
  {
    const round = this.round();
    if ( !round || round.shots_left <= 0 )
      return false;
    round.shots_left--;
    this.persist();
    return true;
  }

  /** Pops a balloon: the prize inside, null for an empty one, undefined if it was gone already. */
  public pop( cell: number ): PrizeShotReward | null | undefined
  {
    const round = this.round();
    if ( !round )
      return undefined;
    const reward = popCell( round, cell );
    if ( reward === undefined )
      return undefined;
    this.persist();
    trackFeatureEvent( "prize_shot_pop", {
      ...this.eventProps( round ),
      cell,
      reward_type: reward?.type ?? "none",
      reward_amount: reward?.amount ?? 0,
      grand_prize: reward?.grand === true,
    }, `${round.round_id}:${round.board_no}:${cell}` );
    return reward;
  }

  /** Prizes still hidden in the shelf. */
  public prizesLeft(): number
  {
    const round = this.round();
    return round ? prizesLeft( round ) : 0;
  }

  public wonRewards(): PrizeShotReward[]
  {
    const round = this.round();
    return round ? wonRewards( round ) : [];
  }

  /** Found since the last claim: what "Take" pays out now. */
  public unclaimedRewards(): PrizeShotReward[]
  {
    const round = this.round();
    return round ? unclaimedRewards( round ) : [];
  }

  /** The pack of balls is used up (or every prize is out): time for a claim. */
  public isBatchOver(): boolean
  {
    const round = this.round();
    return round !== null && isBatchOver( round );
  }

  /**
   * Out of balls with prizes still in the shelf: the screen offers more balls.
   * Once the coin continues are used up only the special offer sells them.
   */
  public canBuyMore(): boolean
  {
    const round = this.round();
    return round !== null && round.shots_left === 0 && !isBoardCleared( round );
  }

  public isBoardCleared(): boolean
  {
    const round = this.round();
    return round !== null && isBoardCleared( round );
  }

  public canContinue(): boolean
  {
    const round = this.round();
    return round !== null && canContinue( round, this.config );
  }

  /** Spends coins on more balls. */
  public async buyContinue(): Promise<PrizeShotContinueResult>
  {
    const round = this.round();
    if ( !round || !canContinue( round, this.config ) )
      return PrizeShotContinueResult.Unavailable;
    const { price, shots } = this.config.continue;
    if ( StatisticsStorage.load().coins < price )
      return PrizeShotContinueResult.InsufficientFunds;

    const payload = { round_id: round.round_id, continue_index: round.continues, price, shots };
    let settlement = this.settlementWithoutServer();

    if ( settlement === null )
    {
      try
      {
        const response = await continuePrizeShot( payload.round_id, payload.continue_index, price, shots );
        adoptEconomyFromPlayer( response.player );
      }
      catch ( e )
      {
        settlement = this.settlementAfterError( e );
        if ( settlement === Settlement.Refused )
        {
          console.warn( "[PrizeShot] continue refused", e );
          if ( e instanceof ApiError && e.status === 402 )
            return PrizeShotContinueResult.InsufficientFunds;
          return PrizeShotContinueResult.Unavailable;
        }
      }
    }

    if ( settlement !== null )
    {
      OfflineEconomy.addCoins( -price );
      if ( settlement === Settlement.Journaled )
        OfflineJournal.append( "prize_shot_continue", payload );
    }

    if ( price > 0 )
    {
      LocalMetrics.report( [ { metric: "spend_coins", amount: price } ] );
      void logVirtualCurrencySpent( { currency_name: "coins", amount: price, sink: "prize_shot_continue" } );
    }
    round.continues++;
    round.shots_left += shots;
    this.persist();
    trackFeatureEvent( "prize_shot_continue", { ...this.eventProps( round ), price, shots } );
    return PrizeShotContinueResult.Ok;
  }

  /** Adds balls from a bought offer (the special offer under the Continue dialog). */
  public addShots( shots: number ): void
  {
    const round = this.round();
    if ( !round || shots <= 0 )
      return;
    round.shots_left += Math.floor( shots );
    this.persist();
  }

  /**
   * Grants what was found since the last claim; returns the rewards merged by type. The board stays (a new one is
   * dealt once every prize is out): the player shoots on or buys more balls.
   */
  public claim(): PrizeShotReward[]
  {
    const round = this.round();
    if ( !round )
      return [];
    if ( round.popped.length <= round.claimed_count && !isBoardCleared( round ) )
    {
      this.persist();
      return [];
    }
    return this.claimRound( round );
  }

  /** What the last period left unclaimed, waiting for the lobby to hand it over; empty when nothing. */
  public pendingRewards(): PrizeShotReward[]
  {
    this.round();
    return this.pending?.rewards ?? [];
  }

  /** Grants the prizes the last period left unclaimed. */
  public claimPending(): PrizeShotReward[]
  {
    const pending = this.pending;
    if ( !pending )
      return [];
    this.pending = null;
    PersistentCache.remove( PENDING_KEY );
    const rewards = sumRewards( pending.rewards );
    this.grantRewardsReported( pending.round_id, rewards );
    trackFeatureEvent( "prize_shot_claimed", {
      event_id: `prize_shot@${pending.round_id}`,
      reason: "period_end",
      rewards_count: rewards.length,
      coins: rewards.find( r => r.type === "coin" )?.amount ?? 0,
    }, `${pending.round_id}:pending:claimed` );
    this.notify();
    return rewards;
  }

  private claimRound( round: PrizeShotRound ): PrizeShotReward[]
  {
    const rewards = sumRewards( unclaimedRewards( round ) );
    round.claimed_count = round.popped.length;
    this.persist();

    this.grantRewardsReported( round.round_id, rewards );
    trackFeatureEvent( "prize_shot_claimed", {
      ...this.eventProps( round ),
      reason: "claim",
      rewards_count: rewards.length,
      coins: rewards.find( r => r.type === "coin" )?.amount ?? 0,
    }, `${round.round_id}:${round.board_no}:claimed:${round.claimed_count}` );

    // Every prize found and paid: a new board, the balls left go on with it.
    if ( isBoardCleared( round ) )
    {
      trackFeatureEvent( "prize_shot_board_completed", this.eventProps( round ), `${round.round_id}:${round.board_no}:completed` );
      dealNextBoard( round, this.config, Math.floor( Math.random() * 0xFFFFFFFF ) );
      this.persist();
    }
    return rewards;
  }

  private grantRewardsReported( round_id: string, rewards: PrizeShotReward[] ): void
  {
    if ( rewards.length === 0 )
      return;
    this.grantRewards( round_id, rewards );
    const coins = rewards.find( r => r.type === "coin" )?.amount ?? 0;
    if ( coins > 0 )
      LocalMetrics.report( [ { metric: "earn_coins", amount: coins } ] );
    logRewardCoins( rewards, "prize_shot" );
  }

  /** On the server when it can; the local economy (and the journal) otherwise. */
  private grantRewards( round_id: string, rewards: PrizeShotReward[] ): void
  {
    const grant_locally = ( settlement: Settlement ) =>
    {
      OfflineEconomy.grantRewards( rewards );
      if ( settlement === Settlement.Journaled )
        OfflineJournal.append( "prize_shot_claim", { round_id, rewards } );
      this.notify();
    };

    const settlement = this.settlementWithoutServer();
    if ( settlement !== null )
    {
      grant_locally( settlement );
      return;
    }

    void claimPrizeShot( round_id, rewards )
      .then( response =>
      {
        adoptEconomyFromPlayer( response.player );
        this.notify();
      } )
      .catch( e =>
      {
        const after_error = this.settlementAfterError( e );
        if ( after_error === Settlement.Refused )
          console.warn( "[PrizeShot] claim refused", e );
        else
          grant_locally( after_error );
      } );
  }

  /** Settled without asking: offline, or the server is known to have no route; null asks the server. */
  private settlementWithoutServer(): Settlement | null
  {
    if ( shouldGoLocal() )
      return Settlement.Journaled;
    if ( this.route_missing )
      return Settlement.LocalOnly;

    return null;
  }

  private settlementAfterError( error: unknown ): Settlement
  {
    if ( error instanceof ApiError && error.status === 404 )
    {
      this.route_missing = true;
      return Settlement.LocalOnly;
    }

    return isNetworkishError( error ) ? Settlement.Journaled : Settlement.Refused;
  }

  /**
   * A round left unfinished when its period ended keeps what was found: the
   * lobby hands it over with the rewards overlay (pendingRewards / claimPending).
   */
  private stashUnclaimed( round: PrizeShotRound ): void
  {
    const rewards = unclaimedRewards( round );
    if ( rewards.length === 0 )
      return;
    const pending: PrizeShotPending = this.pending?.round_id === round.round_id
      ? this.pending
      : { round_id: round.round_id, rewards: [ ...( this.pending?.rewards ?? [] ) ] };
    pending.rewards.push( ...rewards );
    round.claimed_count = round.popped.length;
    this.pending = pending;
    PersistentCache.write( PENDING_KEY, pending );
  }

  private eventProps( round: PrizeShotRound ): Record<string, unknown>
  {
    return {
      event_id: `prize_shot@${round.round_id}`,
      shots_left: round.shots_left,
      popped: round.popped.length,
      continues: round.continues,
      board_no: round.board_no,
    };
  }

  private persist(): void
  {
    if ( this.current )
      PersistentCache.write( ROUND_KEY, this.current );
    this.notify();
  }

  private notify(): void
  {
    this.rev++;
    for ( const listener of this.listeners )
    {
      try
      {
        listener();
      }
      catch ( e )
      {
        console.warn( "[PrizeShot] listener failed", e );
      }
    }
  }

  private readCachedRound(): PrizeShotRound | null
  {
    const round = PersistentCache.read<PrizeShotRound>( ROUND_KEY );
    return round ? migrateRound( round ) : null;
  }

  private readCachedConfig(): PrizeShotConfig | null
  {
    const raw = PersistentCache.read<unknown>( CONFIG_CACHE_KEY );
    return raw ? normalizePrizeShotConfig( raw ) : null;
  }

  // ── QA ──────────────────────────────────────────────────────────────

  /** Deals a fresh board for the current period. */
  public debugReset(): void
  {
    this.current = null;
    this.current_period_start_ms = NaN;
    this.pending = null;
    PersistentCache.remove( PENDING_KEY );
    PersistentCache.remove( ROUND_KEY );
    this.round();
    this.notify();
  }

  public debugSetContinues( continues: number ): void
  {
    const round = this.round();
    if ( !round )
      return;

    round.continues = Math.max( 0, Math.floor( continues ) );
    this.persist();
  }

  public debugSetShots( shots: number ): void
  {
    const round = this.round();
    if ( !round )
      return;

    round.shots_left = Math.max( 0, Math.floor( shots ) );
    this.persist();
  }
}

export const PrizeShotService = new PrizeShotServiceImpl();
